import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ChatProvider } from './context/ChatContext';
import LoginPage from './components/auth/LoginPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ChatPage from './components/chat/ChatPage';
import { useTranslation } from 'react-i18next';
// Initialize i18n
import './i18n/config';
import './App.css';

/**
 * Main application component with routing
 */
function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Login route - redirects to Cognito Hosted UI */}
        <Route path="/login" element={<LoginPage />} />

        {/* OAuth callback route - Cognito redirects here after sign-in */}
        <Route path="/callback" element={<OAuthCallback />} />

        {/* Main chat route - protected */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ChatProvider>
                <ChatPage />
              </ChatProvider>
            </ProtectedRoute>
          }
        />

        {/* Redirect all other routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * OAuth callback handler
 * Cognito redirects here after authentication
 * Handles both success and error cases
 */
function OAuthCallback() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    // Check for OAuth error parameters
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error) {
      // OAuth failed - show error
      setStatus('error');
      setErrorMessage(errorDescription || error);
      return;
    }

    // Check for authorization code (success case)
    const code = params.get('code');
    if (!code) {
      setStatus('error');
      setErrorMessage(t('callback.missing_code'));
      return;
    }

    // Amplify automatically handles the OAuth callback exchange
    // Just redirect to home page after a brief delay
    const timer = setTimeout(() => {
      setStatus('success');
      window.location.href = '/';
    }, 1000);

    return () => clearTimeout(timer);
  }, [t]);

  const handleRetry = () => {
    window.location.href = '/login';
  };

  return (
    <div className="oauth-callback">
      <div className="callback-container">
        {status === 'processing' && <p>{t('callback.processing')}</p>}
        {status === 'success' && <p>{t('callback.success')}</p>}
        {status === 'error' && (
          <div className="error-container">
            <p>{t('callback.error')}</p>
            {errorMessage && <p className="error-detail">{errorMessage}</p>}
            <button onClick={handleRetry} className="btn-primary" type="button">
              {t('callback.try_again')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
